import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getUsersApi, updateUserRoleApi, deleteUserApi, registerStaffApi } from '../lib/api';
import { ArrowLeft, Users, Shield, ShieldCheck, Eye, Trash2, RefreshCw, UserPlus, X, Copy, Check, Link2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';

const ROLE_CONFIG = {
  admin: { label: 'Admin', icon: ShieldCheck, color: 'bg-brand-teal-tint text-brand-teal border-brand-teal/30' },
  operator: { label: 'Operator', icon: Shield, color: 'bg-coaching-tint text-coaching border-coaching/30' },
  viewer: { label: 'Viewer', icon: Eye, color: 'bg-blocks-tint text-blocks border-blocks/30' },
};

export default function ManageUsers() {
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [showInvite, setShowInvite] = useState(false);
  const [inviteForm, setInviteForm] = useState({ name: '', email: '' });
  const [inviting, setInviting] = useState(false);
  const [inviteLink, setInviteLink] = useState(null);
  const [linkCopied, setLinkCopied] = useState(false);

  const fetchUsers = useCallback(async () => {
    try {
      const res = await getUsersApi();
      setUsers(res.data);
    } catch (err) {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const handleRoleChange = async (userId, newRole) => {
    setUpdating(userId);
    try {
      await updateUserRoleApi(userId, newRole);
      setUsers(prev => prev.map(u => u.id === userId ? { ...u, role: newRole } : u));
      toast.success('Role updated');
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to update role';
      toast.error(msg);
    } finally {
      setUpdating(null);
    }
  };

  const handleDelete = async (userId) => {
    setUpdating(userId);
    try {
      await deleteUserApi(userId);
      setUsers(prev => prev.filter(u => u.id !== userId));
      setDeleteConfirm(null);
      toast.success('User removed');
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to delete user';
      toast.error(msg);
    } finally {
      setUpdating(null);
    }
  };

  const isSelf = (userId) => currentUser?.id === userId;

  const handleInvite = async (e) => {
    e.preventDefault();
    if (!inviteForm.name || !inviteForm.email) {
      toast.error('Name and email are required');
      return;
    }
    setInviting(true);
    try {
      const res = await registerStaffApi({ name: inviteForm.name, email: inviteForm.email });
      setUsers(prev => [...prev, { ...res.data, role: res.data.role || 'viewer', invite_status: 'pending' }]);
      const link = `${window.location.origin}/invite/${res.data.invite_token}`;
      setInviteLink(link);
      setInviteForm({ name: '', email: '' });
      toast.success(`${res.data.name || res.data.email} invited`);
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to invite user';
      toast.error(msg);
    } finally {
      setInviting(false);
    }
  };

  const copyInviteLink = () => {
    if (inviteLink) {
      navigator.clipboard.writeText(inviteLink);
      setLinkCopied(true);
      toast.success('Invite link copied');
      setTimeout(() => setLinkCopied(false), 2000);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <RefreshCw className="w-5 h-5 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="manage-users-page">
      {/* Header */}
      <header className="bg-white border-b border-gray-100">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/operator')}
              className="p-1.5 rounded hover:bg-gray-50 transition-colors"
              data-testid="back-to-schedule-btn"
            >
              <ArrowLeft className="w-5 h-5 text-gray-400" />
            </button>
            <div>
              <h1 className="text-base font-medium text-gray-900 flex items-center gap-2">
                <Users className="w-4 h-4 text-gray-500" />
                Manage Users
              </h1>
              <p className="text-xs text-gray-400">{users.length} user{users.length !== 1 ? 's' : ''}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {!showInvite && (
              <Button
                size="sm"
                variant="outline"
                className="h-8 text-xs gap-1.5"
                onClick={() => setShowInvite(true)}
                data-testid="invite-user-btn"
              >
                <UserPlus className="w-3.5 h-3.5" />
                Invite User
              </Button>
            )}
            <button
              onClick={() => { setLoading(true); fetchUsers(); }}
              className="p-1.5 rounded hover:bg-gray-50 transition-colors"
              data-testid="refresh-users-btn"
            >
              <RefreshCw className="w-4 h-4 text-gray-400" />
            </button>
          </div>
        </div>
      </header>

      {/* User List */}
      <main className="max-w-3xl mx-auto px-6 py-6 space-y-3">
        {/* Invite Form */}
        {showInvite && (
          <div
            className="bg-white rounded-lg border border-brand-teal/20 p-4 space-y-3"
            data-testid="invite-user-form"
          >
            {!inviteLink ? (
              <form onSubmit={handleInvite} className="space-y-3">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium text-gray-700">Invite New User</p>
                  <button
                    type="button"
                    onClick={() => { setShowInvite(false); setInviteForm({ name: '', email: '' }); setInviteLink(null); }}
                    className="p-1 rounded hover:bg-gray-100 text-gray-400"
                    data-testid="close-invite-form-btn"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <Input
                    placeholder="Name"
                    value={inviteForm.name}
                    onChange={(e) => setInviteForm(f => ({ ...f, name: e.target.value }))}
                    className="h-9 text-sm"
                    data-testid="invite-name-input"
                  />
                  <Input
                    type="email"
                    placeholder="Email"
                    value={inviteForm.email}
                    onChange={(e) => setInviteForm(f => ({ ...f, email: e.target.value }))}
                    className="h-9 text-sm"
                    data-testid="invite-email-input"
                  />
                </div>
                <div className="flex items-center justify-between">
                  <p className="text-xs text-gray-400">They'll set their own password via invite link</p>
                  <Button
                    type="submit"
                    size="sm"
                    className="h-8 text-xs px-4 bg-gray-900 hover:bg-gray-800"
                    disabled={inviting}
                    data-testid="submit-invite-btn"
                  >
                    {inviting ? 'Inviting...' : 'Send Invite'}
                  </Button>
                </div>
              </form>
            ) : (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium text-gray-700 flex items-center gap-1.5">
                    <Link2 className="w-4 h-4 text-brand-teal" />
                    Invite Link Ready
                  </p>
                  <button
                    type="button"
                    onClick={() => { setShowInvite(false); setInviteLink(null); setLinkCopied(false); }}
                    className="p-1 rounded hover:bg-gray-100 text-gray-400"
                    data-testid="close-invite-link-btn"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
                <div className="flex items-center gap-2">
                  <div className="flex-1 bg-gray-50 border border-gray-200 rounded-md px-3 py-2 text-xs text-gray-600 truncate font-mono" data-testid="invite-link-display">
                    {inviteLink}
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-9 px-3 shrink-0"
                    onClick={copyInviteLink}
                    data-testid="copy-invite-link-btn"
                  >
                    {linkCopied ? <Check className="w-4 h-4 text-brand-teal" /> : <Copy className="w-4 h-4" />}
                  </Button>
                </div>
                <p className="text-xs text-gray-400">Share this link with the user. They'll set their password and can start using the app.</p>
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-7 text-xs text-brand-teal"
                  onClick={() => { setInviteLink(null); setLinkCopied(false); }}
                  data-testid="invite-another-btn"
                >
                  Invite another user
                </Button>
              </div>
            )}
          </div>
        )}
        {users.map((u) => {
          const roleConf = ROLE_CONFIG[u.role] || ROLE_CONFIG.viewer;
          const self = isSelf(u.id);
          const isDeleting = deleteConfirm === u.id;

          return (
            <div
              key={u.id}
              className={`bg-white rounded-lg border p-4 transition-all ${self ? 'border-brand-teal/30 ring-1 ring-brand-teal/10' : 'border-gray-100'}`}
              data-testid={`user-row-${u.id}`}
            >
              <div className="flex items-center justify-between gap-4">
                {/* User info */}
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium text-gray-900 truncate" data-testid={`user-name-${u.id}`}>
                      {u.name || u.email}
                    </p>
                    {self && (
                      <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-brand-teal/30 text-brand-teal">
                        You
                      </Badge>
                    )}
                    {u.invite_status === 'pending' && (
                      <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-status-held/30 text-status-held">
                        Pending
                      </Badge>
                    )}
                  </div>
                  <p className="text-xs text-gray-400 truncate">{u.email}</p>
                </div>

                {/* Role selector + actions */}
                <div className="flex items-center gap-2 shrink-0">
                  <Select
                    value={u.role}
                    onValueChange={(val) => handleRoleChange(u.id, val)}
                    disabled={self || updating === u.id}
                  >
                    <SelectTrigger
                      className={`w-[130px] h-8 text-xs border ${roleConf.color}`}
                      data-testid={`role-select-${u.id}`}
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(ROLE_CONFIG).map(([key, conf]) => (
                        <SelectItem key={key} value={key} data-testid={`role-option-${key}-${u.id}`}>
                          <span className="flex items-center gap-1.5 text-xs">
                            <conf.icon className="w-3 h-3" />
                            {conf.label}
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  {!self && (
                    <>
                      {isDeleting ? (
                        <div className="flex items-center gap-1">
                          <Button
                            size="sm"
                            variant="destructive"
                            className="h-8 text-xs px-2"
                            onClick={() => handleDelete(u.id)}
                            disabled={updating === u.id}
                            data-testid={`confirm-delete-${u.id}`}
                          >
                            Remove
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-8 text-xs px-2"
                            onClick={() => setDeleteConfirm(null)}
                            data-testid={`cancel-delete-${u.id}`}
                          >
                            Cancel
                          </Button>
                        </div>
                      ) : (
                        <button
                          onClick={() => setDeleteConfirm(u.id)}
                          className="p-1.5 rounded hover:bg-blocks-tint text-gray-300 hover:text-blocks transition-colors"
                          data-testid={`delete-btn-${u.id}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          );
        })}

        {users.length === 0 && (
          <div className="text-center py-12 text-sm text-gray-400">
            No users found
          </div>
        )}
      </main>
    </div>
  );
}
